import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { OeMbl } from '../../../fms.core/OeMbl';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { JobNames } from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import MblMilestoneView, { OeMblMilestoneHandle } from './implementation/MblMilestone';

import { useTypedFastStorage } from '../../../hooks/useFastStorage';

import { prepareOeMblForView } from './implementation/mblUtils';
import { OmContainer } from '../../../fms.core/OmContainer';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { SQLInfo } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { getSQLInfoList } from '../../../fms.core/data/TrackingModule';
import SecondaryButton from '../../../ui/SecondaryButton';
import { MblStatusViewModel, OceanMblStatusMap, OeContainerEvents } from '../../../fms.core/MblStatusViewModel';
import { CargoEventLog } from '../../../fms.core/CargoEventLog';
import { AddEventToNotice, CreateCargoEventLog, FindIndexOfKeyStartsWith } from '../../oceanImport/mbl/implementation/mblUtils';
import { formatDateTime } from '../../../dateUtils';
import { CargoEventName } from '../../../framework.core/CargoEventName';
import useSetEditedId from '../../../hooks/useSetEditedId';
import constants from '../../../constants';

const eventMappings = [
  { prefix: "0|", name: CargoEventName.FmsBooking, seq: 9, mappingKey:'0' },
  { prefix: "1|", name: CargoEventName.EmptyOut, seq: 11, mappingKey:'1' },
  { prefix: "250", name: CargoEventName.FullIn, seq: 13, mappingKey:'2' },
  { prefix: "3|", name: CargoEventName.VesselLoaded, seq: 15, mappingKey:'3' },
  { prefix: "270,460", name: CargoEventName.VesselDeparted, seq: 16, mappingKey:'460' },
  { prefix: "5|", name: CargoEventName.CarrierRls, seq: 17, mappingKey:'5' },
  { prefix: "6|", name: CargoEventName.CustomsRls, seq: 18, mappingKey:'6' },
  { prefix: "260,470", name: CargoEventName.VesselArrived, seq: 19, mappingKey:'470' },
  { prefix: "275", name: CargoEventName.VesselDischarged, seq: 20, mappingKey:'8' },
  { prefix: "9|", name: CargoEventName.FmsDlvrToCfs, seq: 5, mappingKey:'9' },
  { prefix: "290", name: CargoEventName.RailDeparted, seq: 21, mappingKey:'10' },
  { prefix: "295", name: CargoEventName.RailArrived, seq: 22, mappingKey:'11' },
  { prefix: "12|", name: CargoEventName.PickAvail, seq: 23, mappingKey:'12' },
  { prefix: "13|", name: CargoEventName.FullOut, seq: 24, mappingKey:'13' },
  { prefix: "14|", name: CargoEventName.Delivered, seq: 25, mappingKey:'14' },
  { prefix: "15|", name: CargoEventName.EmptyIn, seq: 26, mappingKey:'15' },
];

function getStorageKey(id: string) {
  return `oe-mbl-bl-status-${id}`;
}

function MblMilestoneImpl({
  mblId,
  storageKey,
  onClose,
  isView,
}: {
  mblId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);
  const setSavedEntity = useSetEditedId([JobNames.Container]);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);
  
  const [mbl, setMbl, removeMbl, mblRef] = useTypedFastStorage<OeMbl>(storageKey, null);
  const [/*blStatus*/, /*setBlStatus*/, removeBlStatus, /*blStatusRef*/] = useTypedFastStorage<MblStatusViewModel>(getStorageKey(mblId.toString()), null);
  const {setCleanState} = useDirtyCheck(
    storageKey,
    ['updated', 'updatedBy']);

  useEffect(() => {
    initialize();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    cleanup();
    onClose();
  }

  function cleanup() {
    removeMbl();
    removeBlStatus();
  }

  async function initialize() {
    // if (!!mbl) {
    //   setInitialized(true);
    //   return; // Already initialized
    // }
    const apiResult:ApiResult<OeMbl> = await appContext.api.getJobDetail(JobNames.OEMbl, { id: mblId});
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Cannot load data');
      doClose();
      return;
    }

    const loadedMbl = new OeMbl(apiResult.result);
    
    const blApiResult: ApiResult<MblStatusViewModel> = await appContext.api.getMblStatusViewModel(JobNames.OEMbl, mblId);
    loadedMbl.milestone = new MblStatusViewModel(blApiResult?.result ?? null)
    prepareOeMblForView(loadedMbl);

    loadedMbl.milestoneContainerSelectedIndex = loadedMbl.container && loadedMbl.container.length > 0
      ? 0
      : -1;

    const loadedModelObj = new OeMbl(_.cloneDeep(loadedMbl));
    setCleanState(loadedModelObj);
    setMbl(loadedModelObj);
    if(mblRef.current !== null) {
      mblRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function save() {
    if (!mbl || !mblRef.current) {
      throw new Error('Invalid operation');
    }

    if(!mblRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate("T_OOMMAIN", mblRef.current?.id??0, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }

    const blStatusInfoList = getSQLInfoList(mbl as TrackableEntity);
    blStatusInfoList.push(...si);


    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveMblStatusViewModel(blStatusInfoList);
    
    if (apiResult && apiResult.success) {
      await UpdateLogMblMilestone(blStatusInfoList, mbl);    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      setSavedEntity(mblRef.current.id.toString(), false);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }

    doClose();
  }
  
  async function UpdateLogMblMilestone(blStatusInfoList: SQLInfo[], mbl:OeMbl) {
    const cntrDataWithSeq: {
      loc: string,
      cntrNo: string,
      seq: number,
    }[] = []

    const dicEvent = CollectMblEvents(blStatusInfoList, cntrDataWithSeq);
    if (dicEvent.length > 0) {
      const cmile: CargoEventLog[] = [];
      const lastEvents: CargoEventLog[] = [];

      for (const mapping of eventMappings) {
        const dindex = FindIndexOfKeyStartsWith(dicEvent, mapping.prefix);
        if (dindex >= 0) {
          const cargoEventLog = CreateCargoEventLog(mbl.mblNo, mapping.name, dicEvent[dindex], cntrDataWithSeq.filter(x => x.seq === mapping.seq), appContext.user?.id ?? '');
          cmile.push(...cargoEventLog);
        }
      }

      const uniqueCmileSet = new Set(cmile.map(item => item.cntrNo));
      Array.from(uniqueCmileSet).forEach(x => {
        const tempData = cmile.filter(c => c.cntrNo === x).sort((a, b) => new Date(a.utcTime ?? '').getTime() - new Date(b.utcTime ?? '').getTime());
        lastEvents.push(tempData[tempData.length - 1]);
      });

      if (lastEvents.length > 0) {
        const apiResult = await appContext.api.trackingLogMblEvents(JobNames.OEMbl, mblId, lastEvents);;
        if (!apiResult || !apiResult.success) {
          await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        }
      }
    }
  }

  function CollectMblEvents(blStatusInfoList: SQLInfo[], cntrDataWithSeq: { loc: string, cntrNo: string, seq: number }[]) {
    const changeData: { pimsCntrId: number, seqNum: number, dateTime: string }[] = [];
    const dicEvent: { key: string, value: string }[] = [];

    const changeContainerData = blStatusInfoList.filter(x => x.tableName === 'T_OOMCONTAINER');

    OeContainerEvents.forEach((enumEvent) => {
      changeContainerData.forEach(x => {
        const findData = x.fields.find(x => x.propertyName.endsWith(OceanMblStatusMap.get(enumEvent)?.cntrDate ?? ''));
        if (findData) {
          const pimsCntrId = mbl?.container.find(y => y.id === x.key?.value)?.pimsLinkId ?? 0;
          changeData.push({ pimsCntrId: pimsCntrId, seqNum: enumEvent, dateTime: formatDateTime(findData.value) });

          const containerNo = mbl?.container.find(y => y.id === x.key?.value)?.containerNo ?? ''; //GetLocNameFromEvent
          const cntr = mbl?.container.find(x => x.containerNo === containerNo);
          const cntrLoc = OceanMblStatusMap.get(enumEvent)?.cntrLoc ?? '';

          cntrDataWithSeq.push({ loc: Object.getOwnPropertyDescriptor(cntr, cntrLoc)?.value, seq: enumEvent, cntrNo: containerNo })
        }
      })
    });
    changeData.forEach(x => {
      const seqNum = x.seqNum;
      const mappingKey = eventMappings.find(x => x.seq === seqNum)?.mappingKey;
      if (mappingKey)
        AddEventToNotice(dicEvent, `${mappingKey}|${x.dateTime}`, String(x.pimsCntrId));
    });

    return dicEvent;
  }


  return (
    <div className='report-popup'>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }
      {!!mbl &&
        <MblMilestoneInitializedImpl mbl={mbl} setMbl={setMbl} isView={isView} save={save} close={doClose} />
      }
    </div>
  );
}

function MblMilestoneInitializedImpl({
  mbl,
  setMbl,
  isView,
  close,
  save,
}: {
  mbl: OeMbl,
  setMbl: (x:Partial<OeMbl>) => void,
  isView: boolean,
  save: () => Promise<void>,
  close: VoidFunction,
}) {
  const appContext = useContext(ApplicationContext);
  const milestoneRef = useRef<OeMblMilestoneHandle>(null);

  const containerInfo:OmContainer[] = mbl.container ?? [];

  function changeContainerInfo(ci:OmContainer[]) {
    setMbl({ container: ci });
  }

  async function applyToAll() {
    if (milestoneRef.current) {
      milestoneRef.current.executeApplyToAll();
      await save();
    }
  }

  return (
    <>
      <MblMilestoneView
        ref={milestoneRef}
        mbl={mbl}
        onChange={setMbl}
        isView={isView}
        containerInfo={containerInfo}
        onContainerInfoChange={changeContainerInfo}
        user={appContext.user?.id ?? ''}
      />
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={applyToAll}>
          Apply to All Container
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={save}>
          Update
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          onClick={close}>
          Cancel
        </SecondaryButton>
      </div>
    </>
  );
}

const MblMilestonePopup = ({
  mblId,
  storageKey,
  onClose,
  isView,
}:{
  mblId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean,
}) => {
  return (
    <MblMilestoneImpl mblId={mblId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView} />
  );
};

MblMilestonePopup.defaultProps = {
};

MblMilestonePopup.propTypes = {
  mblId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(MblMilestonePopup);
